/**
 * Light, dark, or whatever the machine says.
 *
 * The stylesheet already answers `prefers-color-scheme` and already lets an explicit
 * `data-theme` on the root win in both directions. All this adds is the choice, and the
 * memory of it — kept in `localStorage` rather than in an account, because which theme
 * somebody reads in is a property of the screen they are at, not of who they are. The same
 * person on a phone at night and a desk at noon wants two different answers.
 *
 * `system` is the default and is a real value, not the absence of one: it means "follow the
 * machine", and it has to be choosable again after somebody has picked a side.
 */
export type Theme = "system" | "light" | "dark"

const KEY = "faion.theme"

export function current(): Theme {
  const v = localStorage.getItem(KEY)
  return v === "light" || v === "dark" ? v : "system"
}

export function apply(theme: Theme): void {
  const root = document.documentElement
  if (theme === "system") {
    localStorage.removeItem(KEY)
    delete root.dataset.theme
    return
  }
  localStorage.setItem(KEY, theme)
  root.dataset.theme = theme
}

/** Called once at startup so the stored choice is in place before anything renders. */
export function restore(): void {
  apply(current())
}
